<template>
  <div class="media-carousel">
    <div
      v-if="scene"
      class="overflow-hidden mb-2 h-[30vw] relative media-carousel-scene"
    >
      <div
        ref="scene"
        class="flex items-center h-full bg-black"
      >
        <LazyMediaPlayer
          v-if="activeSlideObject.mediatype === 'video'"
          class="h-full"
          :url="activeSlideObject.url"
          :type="activeSlideObject.type"
          :aspect-ratio="false"
          thumbnail-class="object-cover"
          :autoplay="autoplayScene || autoplay"
        />
        <LazyResponsiveImage
          v-else
          class="size-full object-cover"
          :image="activeSlideObject.url"
          :type="activeSlideObject.type"
          :alt="activeSlideObject.title || 'Stor bild'"
          :loading="lazySceneImage ? 'lazy' : 'eager'"
          :gravity="activeSlideObject.align"
        />
      </div>
    </div>
    <Swiper
      v-bind="swiperProps"
      dots-type="light"
      arrows-type="circular"
      :loop="!scene"
      :rewind="scene"
      :space-between="10"
      :only-render-visible-slides="lazyCarouselImages"
      :initial-slide="initialSlide"
      prevent-swiper-update
      :loading-skeleton-aspect="false"
      loading-skeleton-animation="skeleton-slides"
    >
      <div
        v-for="(slide, slideIndex) in getSlides"
        :key="slideIndex"
        class="h-full block"
        :class="slideClasses(slideIndex)"
      >
        <div
          class="relative size-full"
          :name="`slide-${id}-${slideIndex}`"
          :style="{ transition: changeActiveSlideAnimation }"
          @click="(event) => slideClickAction(slideIndex)"
        >
          <LazyMediaPlayer
            v-if="slide.mediatype === 'video'"
            class="size-full"
            :url="slide.url"
            :type="slide.type"
            :class="{ 'pointer-events-none': allowFullscreen || scene }"
            :aspect-ratio="false"
            :autoplay="autoplay"
            thumbnail-class="object-cover"
          />
          <LazyResponsiveImage
            v-else
            class="size-full object-cover"
            :image="slide.url"
            :type="slide.type"
            :alt="slide.title || `Slide ${slideIndex + 1}`"
            :loading="lazyCarouselImages ? 'lazy' : 'eager'"
            :gravity="slide.align"
          />
          <div
            class="absolute size-full top-100 left-0 bg-dark-black flex justify-center items-center text-white h5 font-medium"
            data-i18n="playingFilm"
          >
            {{ $t('playingFilm') }}
          </div>
        </div>
      </div>
    </Swiper>
    <LazyMediaModal
      v-if="allowFullscreen && showModal"
      :visible="showModal"
      :default-current-index="activeSlideIndex"
      :media-list="modalMedia"
      :title="modalMedia?.[activeSlideIndex]?.title"
      v-bind="modalOptions"
      @on-hide="hideModal"
    />
  </div>
</template>

<script>
import swiperProps from '@packages/swiper/constants/props.js'

export default defineNuxtComponent({
  props: {
    ...swiperProps,

    /**
     * @prop {Array<{
     *   mediatype: image | video
     *   type: cloudinary
     *   url: string
     *   justPreview?: boolean (mediatype = video)
     *   title?: string (alt text)
     * }>} slides
     */
    slides: {
      type: Array,
      required: true,
    },

    autoplay: {
      type: Boolean,
      default: false,
    },

    scene: {
      type: Boolean,
      default: false,
    },

    allowFullscreen: {
      type: Boolean,
      default: false,
    },

    slidesPerView: {
      type: Number,
      default: 1,
    },

    lazySceneImage: {
      type: Boolean,
      default: false,
    },

    lazyCarouselImages: {
      type: Boolean,
      default: true,
    },

    modalProps: {
      type: Object,
      default: () => ({}),
    },
  },

  data () {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    const { slides, allowFullscreen, initialSlide, ...swiperProps } = this.$props

    const changeActiveSlideAnimationDurationMs = 300

    return {
      changeActiveSlideAnimationDurationMs,
      changeActiveSlideAnimation: `transform ease-out ${changeActiveSlideAnimationDurationMs}ms`,
      id: Math.random().toString(),
      swiperProps,
      activeSlideIndex: initialSlide,
      prevActiveSlideIndex: initialSlide,
      activeSlideElementClone: null,
      prevActiveSlideElements: null,
      showModal: false,
      autoplayScene: false,
      modalOptions: {
        size: 'lg',
        showClose: true,
        ...this.$props.modalProps,
      },
    }
  },

  computed: {
    getSlides () {
      if ((this.slides || []).length === 0) {
        return [{
          url: null,
        }]
      }

      return this.slides
        .filter(Boolean)
    },

    activeSlideObject () {
      return this.getSlides[this.isAnimatingChangeActiveSlide ? this.prevActiveSlideIndex : this.activeSlideIndex]
    },

    modalMedia () {
      if (!this.allowFullscreen) {
        return []
      }

      return this.getSlides.map(slide => ({
        ...slide,
        location: slide.url,
      }))
    },
  },

  beforeUnmount () {
    this.resetAnimation()
  },

  methods: {
    slideClickAction (slideIndex) {
      if (this.scene && this.activeSlideIndex !== slideIndex) {
        this.showSlideInScene(slideIndex)
      } else if (this.allowFullscreen) {
        this.activeSlideIndex = slideIndex
        this.displayModal()
      }
    },

    showSlideInScene (slideIndex) {
      this.resetAnimation(true)
      this.activeSlideIndex = slideIndex

      const scene = this.$refs.scene

      // slick carousel adds clones
      const activeSlideElements = document.getElementsByName(`slide-${this.id}-${slideIndex}`)

      if (activeSlideElements && scene) {
        this.prevActiveSlideElements = activeSlideElements

        activeSlideElements.forEach((element) => {
          element.style.transform = 'translateY(-100%)'
        })

        this.activeSlideElementClone = activeSlideElements[0].cloneNode(true)
        this.activeSlideElementClone.className = 'absolute size-full left-0 top-100'
        this.activeSlideElementClone.style.cssText = ''

        scene.appendChild(this.activeSlideElementClone)
        this.isAnimatingChangeActiveSlide = true

        this.changeActiveSlideAnimationTimeout = window.setTimeout(() => {
          scene.style.transition = this.changeActiveSlideAnimation
          scene.style.transform = 'translateY(-100%)'
          this.activeSlideIndex = slideIndex

          this.changeActiveSlideAnimationTimeout = window.setTimeout(() => {
            this.autoplayScene = true
            this.resetAnimation()
          }, this.changeActiveSlideAnimationDurationMs + 100)
        }, this.changeActiveSlideAnimationDurationMs)
      }
    },

    resetAnimation (resetSlide = false) {
      this.hasInteracted = true

      clearTimeout(this.changeActiveSlideAnimationTimeout)
      this.prevActiveSlideIndex = this.activeSlideIndex
      this.isAnimatingChangeActiveSlide = false

      const scene = this.$refs.scene
      if (scene) {
        scene.style.transform = 'unset'
        scene.style.transition = 'unset'

        if (this.activeSlideElementClone) {
          scene.removeChild(this.activeSlideElementClone)
          this.activeSlideElementClone = null
        }
      }

      if (resetSlide && this.prevActiveSlideElements) {
        this.prevActiveSlideElements.forEach(element => (element.style.transform = 'translateY(0)'))
      }
    },

    displayModal () {
      this.showModal = true
    },

    hideModal () {
      this.showModal = false
    },

    slideClasses (slideIndex) {
      return {
        'cursor-zoom-in': this.allowFullscreen && !this.scene,
        'cursor-pointer': this.scene && this.activeSlideIndex !== slideIndex,
      }
    },
  },
})
</script>

<style lang="scss">
.media-carousel {
  &-scene {
    min-height: 390px;
    max-height: 450px;
  }

  .swiper-carousel {
    height: 100%;
  }
}
</style>
